import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Lock, AlertTriangle, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export const SecurityPanel = () => {
  const [migrating, setMigrating] = useState(false);
  const [result, setResult] = useState<{ success: boolean; migrated?: number } | null>(null);

  const handleEncryptAll = async () => {
    setMigrating(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('sensitive-data', {
        body: { action: 'encrypt_all_existing', data: {} },
      });

      if (error) throw error;

      setResult(data);
      toast({
        title: 'Migration terminée',
        description: `${data.migrated} bateau(x) chiffré(s) avec succès.`,
      });
    } catch (err: any) {
      toast({
        title: 'Erreur',
        description: err.message || 'Erreur lors de la migration',
        variant: 'destructive',
      });
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Sécurité & Chiffrement</h2>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-nautic" />
            Chiffrement des données sensibles
          </CardTitle>
          <CardDescription>
            Chiffre les champs HIN, MMSI et immatriculation de tous les bateaux existants en base.
            Les données déjà chiffrées (préfixe <code>enc:</code>) sont ignorées.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
            <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
            <p>Cette opération est irréversible. Assurez-vous que la clé <strong>ENCRYPTION_KEY</strong> est bien configurée dans les secrets Supabase avant de lancer.</p>
          </div>

          <Button onClick={handleEncryptAll} disabled={migrating} className="gap-2">
            <ShieldCheck className="h-4 w-4" />
            {migrating ? 'Migration en cours…' : 'Chiffrer toutes les données existantes'}
          </Button>

          {result && (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
              <CheckCircle className="h-5 w-5" />
              <span>{result.migrated} bateau(x) migré(s) avec succès.</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
